import { yupResolver } from '@hookform/resolvers/yup';
import { Stack } from '@mui/system';
import { UserRoleField } from '@openx/components-administration/lib/UserRoleField/UserRoleField.tsx';
import type { UseUserCreate } from '@openx/components-administration/lib/utils/context/UserHookTypes.ts';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import { OxsUserRole, type OxsUserRoles } from '@openx/types/user.ts';
import { OPEN_AUDIENCE_ORG_EXTERNAL_ID } from '@openx/utils/services/config.ts';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getValidationSchema } from './validationSchema.ts';

type CreateUserModalFormData = {
  first_name: string;
  last_name: string;
  email_address: string;
  role: OxsUserRoles | null;
};

export type CreateUserModalFormProps = {
  handleCreateUser: ReturnType<UseUserCreate>['createUser'];
  organizationId: string;
};

const getInitialValues = (isRoot?: boolean): CreateUserModalFormData => ({
  first_name: '',
  last_name: '',
  email_address: '',
  role: isRoot ? OxsUserRole.ADMIN : null,
});

export const CreateUserModalForm = ({ handleCreateUser, organizationId }: CreateUserModalFormProps) => {
  const { t } = useTranslation();
  const isRootOrganization = organizationId === OPEN_AUDIENCE_ORG_EXTERNAL_ID;

  const formMethods = useForm<CreateUserModalFormData>({
    defaultValues: getInitialValues(isRootOrganization),
    mode: 'all',
    resolver: yupResolver(getValidationSchema(t)),
  });

  const handleSubmit = async (data: CreateUserModalFormData) => {
    const type = isRootOrganization ? 'root' : 'parent';
    const { first_name, last_name, email_address, role } = data;

    await handleCreateUser({
      type,
      user: {
        email_address: email_address,
        first_name: first_name.trim(),
        last_name: last_name.trim(),
        parent_account_id: organizationId,
        role: role || OxsUserRole.MEMBER,
      },
      shouldRedirect: false,
      onSuccess: formMethods.reset,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form data-test="new-user-form" autoComplete="off" noValidate onSubmit={formMethods.handleSubmit(handleSubmit)}>
        <Stack spacing={2} direction="column" sx={{ mb: 2, alignItems: 'baseline', justifyContent: 'end' }}>
          <RHFTextField name={'first_name'} label={t('First Name')} placeholder={t('Enter first name')} fullWidth />
          <RHFTextField name={'last_name'} label={t('Last Name')} placeholder={t('Enter last name')} fullWidth />

          <RHFTextField
            name={'email_address'}
            label={t('Email Address')}
            placeholder={t('Enter email address')}
            fullWidth
          />

          <UserRoleField fullWidth isRootOrganization={isRootOrganization} />

          <Button
            color="primary"
            variant="contained"
            loading={formMethods.formState.isSubmitting}
            type="submit"
            sx={{ width: '100%' }}
          >
            {t('Invite')}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
};
